import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, finalize } from 'rxjs';
import { AdminConsultationService } from '../../../../shared/services/admin-consultation.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { ConfirmMeetingModalComponent } from '../../../../modals/admin/confirm-meeting-modal/confirm-meeting-modal.component';
import { RescheduleBookingModalComponent } from '../../../../modals/admin/reschedule-booking-modal/reschedule-booking-modal.component';
import { CancelBookingModalComponent } from '../../../../modals/admin/cancel-booking-modal/cancel-booking-modal.component';
import {
  IAdminBooking,
  ConsultationBookingStatus,
  PaymentStatus,
} from '../../../../../assets/types/dtoTypes';

@Component({
  selector: 'app-consultation-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './consultation-admin-page.component.html',
  styleUrls: ['./consultation-admin-page.component.scss'],
})
export class ConsultationAdminPageComponent implements OnInit {
  private readonly adminService = inject(AdminConsultationService);
  private readonly dialog = inject(MatDialog);
  private readonly popup = inject(ShowPopupService);

  protected readonly BookingStatus = ConsultationBookingStatus;
  protected readonly PaymentStatus = PaymentStatus;
  protected readonly statusFilters: (ConsultationBookingStatus | '')[] = [
    '',
    ConsultationBookingStatus.PENDING_VALIDATION,
    ConsultationBookingStatus.CONFIRMED,
    ConsultationBookingStatus.COMPLETED,
    ConsultationBookingStatus.CANCELLED,
    ConsultationBookingStatus.PENDING_PAYMENT,
  ];

  bookings = signal<IAdminBooking[]>([]);
  isLoading = signal<boolean>(true);
  activeStatus = signal<ConsultationBookingStatus | ''>(ConsultationBookingStatus.PENDING_VALIDATION);
  busyId = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
  }

  setStatus(status: ConsultationBookingStatus | ''): void {
    this.activeStatus.set(status);
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    const status = this.activeStatus() || undefined;
    this.adminService.listBookings(status)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (rows) => this.bookings.set(rows),
        error: () => this.fail('Could not load bookings'),
      });
  }

  // ----- action availability ---------------------------------------------

  canConfirmMeeting(b: IAdminBooking): boolean {
    return b.status === ConsultationBookingStatus.PENDING_VALIDATION;
  }
  canReschedule(b: IAdminBooking): boolean {
    return b.status === ConsultationBookingStatus.PENDING_VALIDATION
        || b.status === ConsultationBookingStatus.CONFIRMED;
  }
  canCancel(b: IAdminBooking): boolean {
    return b.status !== ConsultationBookingStatus.CANCELLED
        && b.status !== ConsultationBookingStatus.COMPLETED;
  }
  canComplete(b: IAdminBooking): boolean {
    return b.status === ConsultationBookingStatus.CONFIRMED;
  }
  canValidatePayment(b: IAdminBooking): boolean {
    return b.paymentStatus === PaymentStatus.PENDING && !!b.paymentId;
  }

  // ----- actions ----------------------------------------------------------

  confirmMeeting(b: IAdminBooking): void {
    this.dialog.open(ConfirmMeetingModalComponent, { data: { booking: b }, panelClass: 'admin-action-panel' })
      .afterClosed().subscribe((result) => {
        if (result) this.run(this.adminService.confirmMeeting(b.id, result), b.id, 'Meeting confirmed');
      });
  }

  reschedule(b: IAdminBooking): void {
    this.dialog.open(RescheduleBookingModalComponent, { data: { booking: b }, panelClass: 'admin-action-panel' })
      .afterClosed().subscribe((result) => {
        if (result) this.run(this.adminService.rescheduleBooking(b.id, result), b.id, 'Booking rescheduled');
      });
  }

  cancel(b: IAdminBooking): void {
    this.dialog.open(CancelBookingModalComponent, { data: { booking: b }, panelClass: 'admin-action-panel' })
      .afterClosed().subscribe((result) => {
        if (result) this.run(this.adminService.cancelBooking(b.id, result), b.id, 'Booking cancelled');
      });
  }

  complete(b: IAdminBooking): void {
    this.confirm('Mark the consultation as done?', 'This completes the step and unlocks the next one.')
      .subscribe((ok) => {
        if (ok) this.run(this.adminService.completeBooking(b.id), b.id, 'Consultation completed');
      });
  }

  confirmPayment(b: IAdminBooking): void {
    if (!b.paymentId) return;
    this.confirm('Confirm payment received?', 'Use this once the bank transfer has been verified.')
      .subscribe((ok) => {
        if (ok) this.run(this.adminService.confirmPayment(b.paymentId!), b.id, 'Payment confirmed');
      });
  }

  rejectPayment(b: IAdminBooking): void {
    if (!b.paymentId) return;
    this.confirm('Reject this payment?', 'The payment will be marked as failed.')
      .subscribe((ok) => {
        if (ok) this.run(this.adminService.rejectPayment(b.paymentId!), b.id, 'Payment rejected');
      });
  }

  // ----- helpers ----------------------------------------------------------

  private run(call: Observable<IAdminBooking>, id: string, successMsg: string): void {
    this.busyId.set(id);
    call.pipe(finalize(() => this.busyId.set(null))).subscribe({
      next: () => {
        this.popup.showSuccessPopup({ title: successMsg });
        this.load();
      },
      error: (err) => this.fail(err?.error?.message || 'Action failed'),
    });
  }

  private confirm(title: string, message: string) {
    return this.popup.showConfirmationPopup({ status: 'warning', translationKey: '', title, message });
  }

  private fail(message: string): void {
    this.popup.showFailurePopup({ title: 'Error', message });
  }
}
